'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

// Configuration du mode démo
export const DEMO_CONFIG = {
  // Durée de la session démo en secondes (0 = illimité)
  SESSION_DURATION: 0,
  // Nombre maximum de mires par page
  MAX_MIRES_PER_PAGE: 2,
  // Nombre maximum de processeurs sur le workspace
  MAX_PROCESSORS: 2,
  // Nombre maximum d'armoires sur le workspace
  MAX_CABINETS: 1,
  // Sauvegarde désactivée
  SAVE_DISABLED: true,
  // Export PNG uniquement avec watermark
  EXPORT_PNG_ONLY: true,
  // Création de pages désactivée
  PAGE_CREATION_DISABLED: true,
  // Clé localStorage pour le timer
  STORAGE_KEY: 'vg_demo_session',
  // Clé pour marquer la fin de démo
  DEMO_ENDED_KEY: 'vg_demo_ended',
  // Pas de cooldown - relance immédiate possible
  COOLDOWN_HOURS: 0,
};

export interface DemoState {
  isDemo: boolean;
  timeRemaining: number;
  hasEnded: boolean;
  canRestartDemo: boolean;
  cooldownRemaining: number;
  miresCount: number;
}

export interface DemoLimits {
  maxMiresPerPage: number;
  maxProcessors: number;
  maxCabinets: number;
  canSave: boolean;
  canExportPng: boolean;
  canCreatePages: boolean;
}

export interface UseDemoModeReturn {
  state: DemoState;
  limits: DemoLimits;
  startDemo: (onResetWorkspace?: () => void) => void;
  endDemo: () => void;
  resetDemo: () => void;
  checkMireLimit: (currentMiresOnPage: number) => boolean;
  checkProcessorLimit: (currentProcessors: number) => boolean;
  checkCabinetLimit: (currentCabinets: number) => boolean;
  updateMiresCount: (count: number) => void;
  formatTime: (seconds: number) => string;
}

interface StoredDemoSession {
  startTime: number;
  endTime?: number;
}

export function useDemoMode(isPro: boolean): UseDemoModeReturn {
  const [isDemo, setIsDemo] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(DEMO_CONFIG.SESSION_DURATION);
  const [hasEnded, setHasEnded] = useState(false);
  const [canRestartDemo, setCanRestartDemo] = useState(true);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [miresCount, setMiresCount] = useState(0);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const resetWorkspaceRef = useRef<(() => void) | null>(null);

  // Vérifier si l'utilisateur peut relancer une démo
  const checkCooldown = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    const stored = localStorage.getItem(DEMO_CONFIG.DEMO_ENDED_KEY);
    if (stored) {
      const endTime = parseInt(stored, 10);
      const cooldownEnd = endTime + (DEMO_CONFIG.COOLDOWN_HOURS * 60 * 60 * 1000);
      const now = Date.now();
      
      if (now < cooldownEnd) {
        setCanRestartDemo(false);
        setCooldownRemaining(Math.ceil((cooldownEnd - now) / 1000));
      } else {
        setCanRestartDemo(true);
        setCooldownRemaining(0);
        localStorage.removeItem(DEMO_CONFIG.DEMO_ENDED_KEY);
      }
    }
  }, []);

  // Charger la session de démo existante
  useEffect(() => {
    if (typeof window === 'undefined' || isPro) return;
    
    checkCooldown();
    
    // Always clear any previous demo session so users see DemoWelcome first
    // Demo only starts when user explicitly clicks "Start Demo"
    localStorage.removeItem(DEMO_CONFIG.STORAGE_KEY);
  }, [isPro, checkCooldown]);

  // Timer de la démo - DÉSACTIVÉ (pas de limite de temps)
  // La démo reste active indéfiniment jusqu'à ce que l'utilisateur la ferme manuellement

  // Mettre à jour le cooldown
  useEffect(() => {
    if (cooldownRemaining <= 0 || isPro) return;
    
    const interval = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) {
          setCanRestartDemo(true);
          localStorage.removeItem(DEMO_CONFIG.DEMO_ENDED_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [cooldownRemaining, isPro]);

  const startDemo = useCallback((onResetWorkspace?: () => void) => {
    if (isPro || !canRestartDemo) return;
    
    // Store the reset callback for future restarts
    if (onResetWorkspace) {
      resetWorkspaceRef.current = onResetWorkspace;
      // Reset workspace on initial start too
      onResetWorkspace();
    }
    
    const session: StoredDemoSession = {
      startTime: Date.now(),
    };
    localStorage.setItem(DEMO_CONFIG.STORAGE_KEY, JSON.stringify(session));
    setIsDemo(true);
    setHasEnded(false);
    setTimeRemaining(DEMO_CONFIG.SESSION_DURATION);
    setMiresCount(0);
  }, [isPro, canRestartDemo]);

  const endDemo = useCallback(() => {
    setIsDemo(false);
    setHasEnded(true);
    localStorage.removeItem(DEMO_CONFIG.STORAGE_KEY);
    localStorage.setItem(DEMO_CONFIG.DEMO_ENDED_KEY, Date.now().toString());
    if (timerRef.current) clearInterval(timerRef.current);
    checkCooldown();
  }, [checkCooldown]);

  const resetDemo = useCallback(() => {
    localStorage.removeItem(DEMO_CONFIG.STORAGE_KEY);
    localStorage.removeItem(DEMO_CONFIG.DEMO_ENDED_KEY);
    setIsDemo(false);
    setHasEnded(false);
    setCanRestartDemo(true);
    setCooldownRemaining(0);
    setTimeRemaining(DEMO_CONFIG.SESSION_DURATION);
  }, []);

  const checkMireLimit = useCallback((currentMiresOnPage: number): boolean => {
    if (isPro || !isDemo) return true;
    return currentMiresOnPage < DEMO_CONFIG.MAX_MIRES_PER_PAGE;
  }, [isPro, isDemo]);

  const checkProcessorLimit = useCallback((currentProcessors: number): boolean => {
    if (isPro || !isDemo) return true;
    return currentProcessors < DEMO_CONFIG.MAX_PROCESSORS;
  }, [isPro, isDemo]);

  const checkCabinetLimit = useCallback((currentCabinets: number): boolean => {
    if (isPro || !isDemo) return true;
    return currentCabinets < DEMO_CONFIG.MAX_CABINETS;
  }, [isPro, isDemo]);

  const updateMiresCount = useCallback((count: number) => {
    setMiresCount(count);
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const state: DemoState = {
    isDemo,
    timeRemaining,
    hasEnded,
    canRestartDemo,
    cooldownRemaining,
    miresCount,
  };

  const limits: DemoLimits = {
    maxMiresPerPage: DEMO_CONFIG.MAX_MIRES_PER_PAGE,
    maxProcessors: DEMO_CONFIG.MAX_PROCESSORS,
    maxCabinets: DEMO_CONFIG.MAX_CABINETS,
    canSave: !DEMO_CONFIG.SAVE_DISABLED || isPro,
    canExportPng: true, // PNG with watermark is allowed
    canCreatePages: !DEMO_CONFIG.PAGE_CREATION_DISABLED || isPro,
  };

  return {
    state,
    limits,
    startDemo,
    endDemo,
    resetDemo,
    checkMireLimit,
    checkProcessorLimit,
    checkCabinetLimit,
    updateMiresCount,
    formatTime,
  };
}
